/**
 * API + hooks pour /auth (me, login, 2FA) et /users.
 *
 * 2FA TOTP (issue #2) :
 *  - `login` retourne désormais soit `{ totp_required: false, user }` (cookies posés),
 *    soit `{ totp_required: true, challenge_token }` à passer ensuite à `challenge2fa`.
 *  - `enroll2fa` / `verify2fa` / `disable2fa` gèrent l'enrôlement depuis Réglages → Sécurité.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  OrganizationRead,
  OrganizationUpdate,
  UserCreate,
  UserCreated,
  UserRead,
  UserUpdate,
} from "./schema";

// ── 2FA types (mirrors backend/app/schemas/auth.py) ─────────────────────────

export interface LoginResponse {
  user: UserRead | null;
  totp_required: boolean;
  challenge_token: string | null;
}

export interface TOTPEnrollResponse {
  secret: string;
  provisioning_uri: string;
  qr_png_base64: string;
}

export interface TOTPVerifyResponse {
  recovery_codes: string[];
}

export interface SignupPayload {
  organization_name: string;
  full_name: string;
  email: string;
  password: string;
}

export interface SignupResponse {
  user: UserRead;
  organization: OrganizationRead;
}

/**
 * Routes the onboarding guard redirects to. Each one must be declared in the
 * router: an unknown path falls through to the catch-all and bounces the admin
 * back to the home page (see `router.test.tsx`).
 */
export const ONBOARDING_ROUTES = ["/", "/reglages", "/dossiers/flash"] as const;
export type OnboardingRoute = (typeof ONBOARDING_ROUTES)[number];

export interface OnboardingStatusResponse {
  organization_id: string;
  is_admin: boolean;
  /** #549 — l'adresse du premier admin doit être prouvée avant COMPLETE. */
  email_verified: boolean;
  cabinet_profile_completed: boolean;
  team_setup_completed: boolean;
  /** Sortie explicite « je travaille seul » pour un cabinet sans équipe. */
  team_setup_skipped: boolean;
  first_dossier_completed: boolean;
  completed: boolean;
  next_step: "EMAIL_VERIFICATION" | "CABINET_PROFILE" | "TEAM_SETUP" | "FIRST_DOSSIER" | "COMPLETE";
  next_route: OnboardingRoute;
  active_user_count: number;
  /** Membres invités qui ne se sont encore jamais connectés. */
  pending_invitation_count: number;
  /** Membres (hors soi-même) qui ont effectivement rejoint le cabinet. */
  accepted_teammate_count: number;
  dossier_count: number;
}

// ───────────────────────────────────────────────────────────────────────────

export const authApi = {
  me: () => api.get<UserRead>("/auth/me"),
  login: (email: string, password: string) =>
    api.post<LoginResponse>("/auth/login", { email, password }),
  signup: (payload: SignupPayload) => api.post<SignupResponse>("/auth/signup", payload),
  logout: () => api.post<void>("/auth/logout"),
  organization: () => api.get<OrganizationRead>("/auth/organization"),
  onboardingStatus: () => api.get<OnboardingStatusResponse>("/auth/onboarding-status"),
  requestEmailVerification: () => api.post<{ detail: string }>("/auth/verify-email/request"),
  confirmEmailVerification: (token: string) =>
    api.post<void>("/auth/verify-email/confirm", { token }),
  skipTeamSetup: () => api.post<OnboardingStatusResponse>("/auth/onboarding/skip-team"),
  updateOrganization: (payload: OrganizationUpdate) =>
    api.patch<OrganizationRead>("/auth/organization", payload),

  // 2FA TOTP (issue #2)
  enroll2fa: () => api.post<TOTPEnrollResponse>("/auth/2fa/enroll"),
  verify2fa: (code: string) => api.post<TOTPVerifyResponse>("/auth/2fa/verify", { code }),
  disable2fa: (password: string, code: string) =>
    api.post<void>("/auth/2fa/disable", { password, code }),
  challenge2fa: (challenge_token: string, code: string) =>
    api.post<UserRead>("/auth/2fa/challenge", { challenge_token, code }),

  // Self-service password reset (#167)
  forgotPassword: (email: string) =>
    api.post<{ detail: string }>("/auth/forgot-password", { email }),
  resetPassword: (token: string, new_password: string) =>
    api.post<void>("/auth/reset-password", { token, new_password }),
  // Signed-in change: revokes every other session, refreshes this one's cookies.
  changePassword: (current_password: string, new_password: string) =>
    api.post<UserRead>("/auth/change-password", { current_password, new_password }),
};

export const usersApi = {
  list: () => api.get<UserRead[]>("/users"),
  create: (payload: UserCreate) => api.post<UserCreated>("/users", payload),
  update: (id: string, payload: UserUpdate) => api.patch<UserRead>(`/users/${id}`, payload),
  delete: (id: string) => api.delete<void>(`/users/${id}`),
  deletePermanent: (id: string) => api.delete<void>(`/users/${id}/permanent`),
  resetPassword: (id: string) => api.post<UserCreated>(`/users/${id}/reset-password`),
  resendInvitation: (id: string) => api.post<UserCreated>(`/users/${id}/resend-invitation`),
  unlock: (id: string) => api.post<void>(`/users/${id}/unlock`),
  reset2fa: (id: string) => api.post<UserRead>(`/users/${id}/reset-2fa`),
  changeEmail: (id: string, email: string) => api.post<UserRead>(`/users/${id}/email`, { email }),
};

export const usersKeys = {
  all: ["users"] as const,
  list: () => ["users", "list"] as const,
  organization: () => ["organization"] as const,
};

export const authKeys = {
  all: ["auth"] as const,
  onboardingStatus: () => ["auth", "onboarding-status"] as const,
  securityPolicy: () => ["auth", "security-policy"] as const,
};

/** Interrupteurs de déploiement : la 2FA est-elle active, et exigée des ADMIN ? */
export interface SecurityPolicyRead {
  totp_enabled: boolean;
  totp_required_for_admin: boolean;
  totp_grace_period_days: number;
}

export function useSecurityPolicy() {
  return useQuery({
    queryKey: authKeys.securityPolicy(),
    queryFn: () => api.get<SecurityPolicyRead>("/auth/security-policy"),
    staleTime: 10 * 60 * 1000,
  });
}

export function useOnboardingStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: authKeys.onboardingStatus(),
    queryFn: () => authApi.onboardingStatus(),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/** Ré-envoyer le lien de vérification d'adresse (#549). */
export function useRequestEmailVerification() {
  return useMutation({
    mutationFn: () => authApi.requestEmailVerification(),
  });
}

/** Marquer l'étape équipe comme volontairement passée — cabinet solo (#549). */
export function useSkipTeamSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.skipTeamSetup(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authKeys.onboardingStatus() });
    },
  });
}

export function useUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: usersKeys.list(),
    queryFn: () => usersApi.list(),
    enabled: options.enabled ?? true,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserCreate) => usersApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; payload: UserUpdate }) =>
      usersApi.update(vars.id, vars.payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useDeleteUserPermanent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deletePermanent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.resendInvitation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.unlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useResetUser2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.reset2fa(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useChangeUserEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) => usersApi.changeEmail(id, email),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationUpdate) => authApi.updateOrganization(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.organization() }),
  });
}
